"use client";

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import type { ShopifyCart } from "@/types/shopify";
import { CART_LINE_QUANTITY_MAX } from "@/lib/cart/limits";

// ─────────────────────────────────────────────────────────
// State
// ─────────────────────────────────────────────────────────

interface CartState {
  cart: ShopifyCart | null;
  isOpen: boolean;
  isLoading: boolean;
  pendingLineId: string | null;
  error: string | null;
}

type CartAction =
  | { type: "SET_CART"; cart: ShopifyCart }
  | { type: "CLEAR_CART" }
  | { type: "OPEN_CART" }
  | { type: "CLOSE_CART" }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_PENDING_LINE"; lineId: string | null }
  | { type: "SET_ERROR"; error: string | null };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "SET_CART":
      return { ...state, cart: action.cart, isLoading: false, pendingLineId: null, error: null };
    case "CLEAR_CART":
      return { ...state, cart: null, isLoading: false, pendingLineId: null, error: null };
    case "OPEN_CART":
      return { ...state, isOpen: true };
    case "CLOSE_CART":
      return { ...state, isOpen: false };
    case "SET_LOADING":
      return { ...state, isLoading: action.loading };
    case "SET_PENDING_LINE":
      return { ...state, pendingLineId: action.lineId };
    case "SET_ERROR":
      return { ...state, error: action.error, isLoading: false, pendingLineId: null };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────

interface CartContextValue extends CartState {
  addItem: (
    merchandiseId: string,
    quantity?: number,
    attributes?: { key: string; value: string }[]
  ) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  updateItemQuantity: (lineId: string, quantity: number) => Promise<void>;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

// ─────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────

const CART_ID_KEY = "aurora_cart_id";

async function parseCartResponse(res: Response): Promise<{ cart: ShopifyCart | null }> {
  const data = await res.json().catch(() => null) as { cart?: ShopifyCart | null; error?: unknown } | null;
  if (!res.ok) {
    throw new Error(typeof data?.error === "string" ? data.error : "Cart request failed");
  }
  if (!data || !("cart" in data)) {
    throw new Error("Cart response was incomplete. Please try again.");
  }
  if (data.cart !== null && (typeof data.cart !== "object" || typeof data.cart.id !== "string")) {
    throw new Error("Cart response was incomplete. Please try again.");
  }
  return { cart: data.cart ?? null };
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, {
    cart: null,
    isOpen: false,
    isLoading: false,
    pendingLineId: null,
    error: null,
  });

  // Restore cart on mount
  useEffect(() => {
    const cartId = localStorage.getItem(CART_ID_KEY);
    if (!cartId) return;

    dispatch({ type: "SET_LOADING", loading: true });
    fetch(`/api/cart/get?cartId=${encodeURIComponent(cartId)}`)
      .then(parseCartResponse)
      .then((data) => {
        if (data.cart) {
          dispatch({ type: "SET_CART", cart: data.cart });
        } else {
          localStorage.removeItem(CART_ID_KEY);
          dispatch({ type: "CLEAR_CART" });
        }
      })
      .catch(() => {
        localStorage.removeItem(CART_ID_KEY);
        dispatch({ type: "CLEAR_CART" });
      });
  }, []);

  const addItem = useCallback(
    async (
      merchandiseId: string,
      quantity = 1,
      attributes?: { key: string; value: string }[]
    ) => {
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > CART_LINE_QUANTITY_MAX) {
        const validationError = new Error(`Choose a quantity from 1 to ${CART_LINE_QUANTITY_MAX}.`);
        dispatch({ type: "SET_ERROR", error: validationError.message });
        throw validationError;
      }
      dispatch({ type: "SET_LOADING", loading: true });
      try {
        const cartId = localStorage.getItem(CART_ID_KEY);
        const res = await fetch("/api/cart/add", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cartId,
            lines: [{ merchandiseId, quantity, attributes }],
          }),
        });
        const data = await parseCartResponse(res);
        if (!data.cart) throw new Error("Cart response was incomplete. Please try again.");
        localStorage.setItem(CART_ID_KEY, data.cart.id);
        dispatch({ type: "SET_CART", cart: data.cart });
        dispatch({ type: "OPEN_CART" });
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Unknown error",
        });
        // Callers need to know that no cart mutation was confirmed. This is
        // what lets product and quick-add UIs show an actionable error instead
        // of closing as if the item had been added.
        throw err;
      }
    },
    []
  );

  const removeItem = useCallback(async (lineId: string) => {
    const cartId = state.cart?.id;
    if (!cartId) return;
    dispatch({ type: "SET_PENDING_LINE", lineId });
    try {
      const res = await fetch("/api/cart/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cartId, lineId }),
      });
      const data = await parseCartResponse(res);
      if (!data.cart) throw new Error("Cart response was incomplete. Please try again.");
      dispatch({ type: "SET_CART", cart: data.cart });
    } catch (err) {
      dispatch({
        type: "SET_ERROR",
        error: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [state.cart?.id]);

  const updateItemQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      const cartId = state.cart?.id;
      if (!cartId) return;
      dispatch({ type: "SET_PENDING_LINE", lineId });
      try {
        const res = await fetch("/api/cart/update", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cartId, lineId, quantity }),
        });
        const data = await parseCartResponse(res);
        if (!data.cart) throw new Error("Cart response was incomplete. Please try again.");
        dispatch({ type: "SET_CART", cart: data.cart });
      } catch (err) {
        dispatch({
          type: "SET_ERROR",
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [state.cart?.id]
  );

  const clearCart = useCallback(() => {
    localStorage.removeItem(CART_ID_KEY);
    dispatch({ type: "CLEAR_CART" });
  }, []);
  const openCart = useCallback(() => dispatch({ type: "OPEN_CART" }), []);
  const closeCart = useCallback(() => dispatch({ type: "CLOSE_CART" }), []);

  return (
    <CartContext.Provider
      value={{
        ...state,
        addItem,
        removeItem,
        updateItemQuantity,
        clearCart,
        openCart,
        closeCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
